import { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import host from "../../global";
import usePurchaseOptions from "../../hooks/usePurchaseOptions";
import { formatDate, formatCount } from "./billingFormat";

// At or below this share left, the balance turns amber and says when it runs out.
const LOW_SHARE = 0.2;

// "1 interview", "2 interviews".
const count = (n, one) => `${formatCount(n)} ${one}${Number(n) === 1 ? "" : "s"}`;
const ENDING_DAYS = 7;

/**
 * One slim row under the header on every page:
 *   - the running balance (interviews and CV screenings left, and until when),
 *     amber when low, with the date the current pace runs out;
 *   - or, in the last 7 days, what expires and when;
 *   - or, once the plan has ended, that it has and what to do.
 *
 * `role` is "admin" (can buy) or "hr" (asks the admin).
 */
export default function PlanStatusBar({ role = "admin" }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Loaded by the effect below (on mount and on every page change).
  const { options, reload } = usePurchaseOptions({ enabled: false });
  const [asked, setAsked] = useState(false);

  // Balances change as people work; refresh when they move between pages.
  useEffect(() => { reload(); }, [location.pathname, reload]);

  const current = options?.current;
  // Never on Free: the dashboard's own trial banner already covers it.
  if (!current || current.is_trial) return null;
  // Only on the dashboard and the usage page (Plans & Payments, also reached
  // as /plans). Nowhere else.
  if (!/\/(dashboard|usage|plans)\/?$/.test(location.pathname)) return null;

  const isAdmin = role === "admin" && options.can_buy;
  const iv = options.projections?.interviews || {};
  const cv = options.projections?.cvs || {};
  const plansUrl = "/org-admin/usage";

  const askAdmin = async (ended) => {
    try {
      await axios.post(
        `${host}/notify-admin-limit`,
        { featureLabel: ended ? "a plan renewal" : "interviews", ended },
        { headers: { Authorization: `Bearer ${Cookies.get("accessToken")}` } }
      );
      setAsked(true);
      toast.success("We've let your admin know.");
    } catch {
      toast.error("Couldn't reach your admin. Please try again.");
    }
  };

  const action = (label, onClick) => (
    <button
      onClick={onClick}
      className="shrink-0 px-3 py-1 rounded-full text-[12px] font-semibold text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90"
    >
      {label}
    </button>
  );

  // ---- ended ----
  if (!current.in_date) {
    return (
      <div className="mb-4 rounded-lg px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 bg-[#FBEAEA] dark:bg-red-900/25 text-[#B03535] dark:text-red-300 text-[13px]">
        <span>
          {isAdmin
            ? `Your ${current.plan_name} plan ended on ${formatDate(current.ends_on)}. Choose a plan to keep screening and interviewing. Your jobs and candidates are kept.`
            : `Your team's plan ended on ${formatDate(current.ends_on)}. Your admin can renew it.`}
        </span>
        {isAdmin
          ? action("See plans", () => navigate(plansUrl))
          : !asked && action("Let my admin know", () => askAdmin(true))}
      </div>
    );
  }

  // ---- ending soon (admins only) ----
  const daysLeft = iv.days_left ?? cv.days_left ?? 99;
  if (isAdmin && daysLeft <= ENDING_DAYS) {
    const leftovers = [];
    if (iv.left) leftovers.push(count(iv.left, "interview"));
    if (cv.left) leftovers.push(count(cv.left, "CV screening"));
    const canMoveUp = Object.values(options.plans || {}).some((p) => p.allowed && p.kind === "upgrade");
    return (
      <div className="mb-4 rounded-lg px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 bg-[#FDF3E1] dark:bg-amber-900/25 text-[#8A5508] dark:text-amber-300 text-[13px]">
        <span>
          Your plan ends on {formatDate(current.ends_on)}.
          {leftovers.length
            ? ` ${leftovers.join(" and ")} expire then${canMoveUp ? ", unless you move to a bigger plan first" : ""}.`
            : " Renew to keep screening and interviewing without a break."}
        </span>
        {action("See plans", () => navigate(plansUrl))}
      </div>
    );
  }

  // ---- running balance ----
  const low = (p) => p.total > 0 && p.left / p.total <= LOW_SHARE;
  const ivLow = low(iv);
  const cvLow = low(cv);
  const lowOne = ivLow ? iv : cvLow ? cv : null;
  // Only when there's something to act on: low here, last 7 days and ended
  // above. A healthy balance shows nothing.
  if (!lowOne) return null;
  return (
    <div className={`mb-4 rounded-lg px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-[12.5px] ${
      lowOne
        ? "bg-[#FDF3E1] dark:bg-amber-900/25 text-[#8A5508] dark:text-amber-300"
        : "bg-white/70 dark:bg-white/5 text-[#6B7280] dark:text-gray-400 border border-[#EBEAED] dark:border-gray-700/60"
    }`}>
      <span>
        <b className={lowOne ? "" : "text-[#404040] dark:text-gray-200"}>{count(iv.left, "interview")}</b>
        {" "}and{" "}
        <b className={lowOne ? "" : "text-[#404040] dark:text-gray-200"}>{count(cv.left, "CV screening")}</b>
        {" "}left on your {current.plan_name} plan, until {formatDate(current.ends_on)}
        {lowOne?.runs_out_on
          ? `. At your pace the ${lowOne === iv ? "interviews" : "CV screenings"} last until about ${formatDate(lowOne.runs_out_on, { year: false })}.`
          : "."}
      </span>
      {lowOne && (isAdmin
        ? action("Get more", () => navigate(
            options.topup?.allowed
              ? `${plansUrl}?topup=${Math.max(1, options.recommendation?.packs || 1)}`
              : plansUrl
          ))
        : !asked && action("Let my admin know", () => askAdmin(false)))}
    </div>
  );
}
